// src/pages/veci/DirectorioVeci.js
//
// Directorio real del veci contra el backend.
// - Carga negocios del backend con filtros: categoría, texto, GPS + radio.
// - Botón "Usar mi ubicación" para ordenar por distancia.
// - Slider de radio (1–10 km) — el veci ajusta hasta dónde busca.
// - Chip Abierto/Cerrado desde AbiertoAhora.
// - Chip de distancia si viene DistanciaKm.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardActionArea, CardContent, Grid, Chip,
  TextField, InputAdornment, Slider, Button, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CreditScoreIcon from '@mui/icons-material/CreditScore';

import { useApp } from '../../context/AppContext';
import { negocios as apiNegocios } from '../../api/negociosApi';
import { catalogos } from '../../api/registroApi';

export default function DirectorioVeci() {
  const { sesionBackend } = useApp();
  const navigate = useNavigate();

  const [texto, setTexto]         = useState('');
  const [categoriaId, setCatId]   = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [coords, setCoords]       = useState({ lat: null, lng: null });
  const [radioKm, setRadioKm]     = useState(2);

  const [items, setItems]         = useState([]);
  const [cargando, setCargando]   = useState(false);
  const [error, setError]         = useState('');
  const [ubicando, setUbicando]   = useState(false);

  // Cargar categorías de negocio para el chip bar
  useEffect(() => {
    (async () => {
      try {
        const r = await catalogos.categoriasNegocio();
        setCategorias(Array.isArray(r) ? r : (r.items || []));
      } catch { /* silent */ }
    })();
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const params = {
        q: texto || undefined,
        categoriaNegocioId: categoriaId || undefined,
        lat: coords.lat || undefined,
        lng: coords.lng || undefined,
        radioKm: coords.lat ? radioKm : undefined,
        pageSize: 50,
      };
      const r = await apiNegocios.listar(params);
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cargar el directorio.');
    } finally { setCargando(false); }
  }, [texto, categoriaId, coords, radioKm]);

  useEffect(() => { cargar(); }, [cargar]);

  const usarUbicacion = () => {
    if (!navigator.geolocation) { setError('Este navegador no soporta GPS.'); return; }
    setUbicando(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUbicando(false);
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        setUbicando(false);
        setError('No se pudo obtener tu ubicación. Activa el permiso del navegador.');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const primerNombre = (sesionBackend?.nombre || '').split(' ')[0];

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Directorio</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>
          Hola{primerNombre ? `, ${primerNombre}` : ''}
        </Typography>
        <Typography color="text.secondary">
          Estos son los negocios del sector que están recibiendo pedidos.
        </Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <TextField
        fullWidth
        placeholder="Buscar un negocio…"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
        sx={{ bgcolor: 'background.paper' }}
      />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant={coords.lat ? 'contained' : 'outlined'}
            startIcon={<MyLocationIcon />}
            onClick={usarUbicacion}
            disabled={ubicando}
          >
            {ubicando ? 'Ubicando…' : (coords.lat ? 'Ubicación activa' : 'Usar mi ubicación')}
          </Button>
          {coords.lat && (
            <Button size="small" onClick={() => setCoords({ lat: null, lng: null })}>
              Quitar ubicación
            </Button>
          )}
        </Box>
        {coords.lat && (
          <Box sx={{ px: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Radio de búsqueda: <strong>{radioKm} km</strong>
            </Typography>
            <Slider
              value={radioKm}
              onChange={(_, v) => setRadioKm(v)}
              min={1} max={10} step={1}
              valueLabelDisplay="auto"
              sx={{ maxWidth: 400 }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label="Todos"
          onClick={() => setCatId(null)}
          color={!categoriaId ? 'primary' : 'default'}
          variant={!categoriaId ? 'filled' : 'outlined'}
        />
        {categorias.map((c) => {
          const id = c.categoriaNegocioId || c.CategoriaNegocioId || c.id;
          const nombre = c.nombre || c.Nombre;
          return (
            <Chip
              key={id}
              label={nombre}
              onClick={() => setCatId(id)}
              color={categoriaId === id ? 'primary' : 'default'}
              variant={categoriaId === id ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">
              No encontramos negocios con estos filtros. Prueba ampliando el radio o cambiando la categoría.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {items.map((n) => (
            <Grid key={n.id || n.Id} item xs={12} sm={6} md={4}>
              <TarjetaNegocio
                n={n}
                onClick={() => navigate(`/veci/negocio/${n.id || n.Id}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}

function TarjetaNegocio({ n, onClick }) {
  const color         = n.color || n.Color || '#0F4C3A';
  const nombre        = n.nombre || n.Nombre;
  const direccion     = n.direccion || n.Direccion;
  const horario       = n.horario || n.Horario;
  const distancia     = n.distanciaKm ?? n.DistanciaKm;
  const abierto       = n.abiertoAhora ?? n.AbiertoAhora;
  const aceptaFiado   = n.aceptaFiado ?? n.AceptaFiado;
  const compromiso    = n.minutosCompromiso ?? n.MinutosCompromiso;
  const categoriaNombre = (n.categoria || n.Categoria)?.nombre;

  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <Box sx={{ bgcolor: color, height: 8 }} />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Fraunces', flexGrow: 1 }}>
              {nombre}
            </Typography>
            {abierto != null && (
              <Chip
                size="small"
                label={abierto ? 'Abierto' : 'Cerrado'}
                color={abierto ? 'success' : 'default'}
                sx={{ flexShrink: 0 }}
              />
            )}
          </Box>

          {categoriaNombre && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {categoriaNombre}
            </Typography>
          )}

          {direccion && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {direccion}
            </Typography>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {distancia != null && (
              <Chip size="small" label={`${distancia.toFixed(1)} km`} variant="outlined" />
            )}
            {horario && (
              <Tooltip title={horario}>
                <Chip
                  size="small"
                  icon={<ScheduleIcon />}
                  label={horario.length > 20 ? `${horario.substring(0, 20)}…` : horario}
                  variant="outlined"
                />
              </Tooltip>
            )}
            {compromiso && (
              <Chip size="small" label={`~${compromiso} min`} variant="outlined" color="info" />
            )}
            {aceptaFiado && (
              <Chip size="small" icon={<CreditScoreIcon />} label="Fía" variant="outlined" color="warning" />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
